import { cn, getWeekdayShortLabel } from '@quadralize/utils'
import * as React from 'react'
import { DAYS_PER_WEEK, VARIANT_ACCENT } from './constants'
import type {
  PositionedEvent,
  SchedulerDensity,
  SchedulerEvent,
  SchedulerEventRenderContext,
  SchedulerProps,
  SchedulerResource,
  SchedulerSlotRenderContext,
} from './types'
import { buildMonthDays, hourToTimeString, toIso } from './utils'

const MAX_VISIBLE_EVENTS_PER_DAY = 3
const WEEKDAY_INDEX_SUNDAY = 0

interface MonthGridProps {
  date: Date
  density?: SchedulerDensity
  resources: SchedulerResource[]
  activeResourceId?: string
  events: SchedulerEvent[]
  startTime: number
  isLoading?: boolean
  isMobile: boolean
  onDateChange?: (date: Date) => void
  onSlotClick?: SchedulerProps['onSlotClick']
  onEventClick?: SchedulerProps['onEventClick']
  onEventHover?: SchedulerProps['onEventHover']
  renderEvent?: SchedulerProps['renderEvent']
  renderSlot?: SchedulerProps['renderSlot']
}

export function MonthGrid({
  date,
  density = 'comfortable',
  resources,
  activeResourceId,
  events,
  startTime,
  isLoading = false,
  isMobile,
  onDateChange,
  onSlotClick,
  onEventClick,
  onEventHover,
  renderEvent,
  renderSlot,
}: MonthGridProps): React.JSX.Element {
  const activeMonth = date.getMonth()
  const activeDateIso = toIso(date)
  const todayIso = toIso(new Date())
  const days = React.useMemo(() => buildMonthDays(date), [date])

  const selectedResourceId = activeResourceId ?? resources[0]?.id
  const selectedResource = resources.find((resource) => resource.id === selectedResourceId)
  const defaultSlotTime = hourToTimeString(startTime)

  const resourceMap = React.useMemo(
    () => new Map(resources.map((resource) => [resource.id, resource])),
    [resources],
  )

  const eventsByDate = React.useMemo(() => {
    const map = new Map<string, SchedulerEvent[]>()

    const sourceEvents = selectedResourceId
      ? events.filter((event) => event.resourceId === selectedResourceId)
      : events

    for (const event of sourceEvents) {
      if (!event.date) {
        continue
      }

      const list = map.get(event.date) ?? []
      list.push(event)
      map.set(event.date, list)
    }

    for (const [dayIso, dayEvents] of map) {
      map.set(
        dayIso,
        dayEvents.sort((a, b) => a.startTime.localeCompare(b.startTime)),
      )
    }

    return map
  }, [events, selectedResourceId])

  const weekdayHeaders = React.useMemo(() => {
    return Array.from({ length: DAYS_PER_WEEK }, (_, index) => {
      const weekdayIndex = (WEEKDAY_INDEX_SUNDAY + index) % DAYS_PER_WEEK
      return getWeekdayShortLabel(weekdayIndex)
    })
  }, [])
  const isCompact = density === 'compact'

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="grid grid-cols-7 border-b border-border-default bg-bg-subtle/85 supports-backdrop-filter:backdrop-blur-sm">
        {weekdayHeaders.map((label) => (
          <div
            key={label}
            className="px-2 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wide text-text-tertiary"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-7 overflow-auto">
        {days.map((day) => {
          const dayIso = toIso(day)
          const dayEvents = eventsByDate.get(dayIso) ?? []
          const isToday = dayIso === todayIso
          const isActiveDate = dayIso === activeDateIso
          const isCurrentMonth = day.getMonth() === activeMonth
          const visibleEvents = dayEvents.slice(0, MAX_VISIBLE_EVENTS_PER_DAY)
          const remainingEvents = dayEvents.length - visibleEvents.length

          return (
            <div
              key={dayIso}
              className={cn(
                'group relative flex flex-col gap-1 border-r border-b border-border-subtle transition-colors',
                isCompact
                  ? 'min-h-20 px-1 py-1 sm:min-h-24 sm:px-1.5 sm:py-1.5'
                  : 'min-h-24 px-1.5 py-1.5 sm:min-h-30 sm:px-2 sm:py-2',
                !isCurrentMonth && 'bg-bg-subtle/40',
                isCurrentMonth && !isActiveDate && 'hover:bg-bg-muted/50',
                isActiveDate && 'bg-primary/5 ring-1 ring-inset ring-primary/15',
              )}
            >
              <div className="flex items-center justify-between gap-1">
                <button
                  type="button"
                  onClick={() => onDateChange?.(day)}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                    isCompact && 'h-5 w-5 text-[11px]',
                    isCurrentMonth ? 'text-text-primary' : 'text-text-tertiary',
                    isToday && 'bg-primary text-white',
                    !isToday && isActiveDate && 'bg-primary/10 text-primary',
                  )}
                  aria-label={`Selecionar dia ${day.getDate()}`}
                >
                  {day.getDate()}
                </button>

                {selectedResourceId && onSlotClick && isCurrentMonth && (
                  <button
                    type="button"
                    onClick={() => onSlotClick(selectedResourceId, defaultSlotTime, dayIso)}
                    className={cn(
                      'rounded-md border border-dashed border-border-default/70 bg-bg-base/70 font-medium text-text-tertiary transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                      isCompact ? 'px-1 py-0 text-[9px]' : 'px-1.5 py-0.5 text-[10px]',
                    )}
                    aria-label={`Agendar em ${dayIso}`}
                  >
                    {renderSlot?.({
                      resourceId: selectedResourceId,
                      resource: selectedResource,
                      time: defaultSlotTime,
                      date: dayIso,
                      view: 'month',
                      activeDate: date,
                      isMobile,
                    } as SchedulerSlotRenderContext) ?? '+ Ag.'}
                  </button>
                )}
              </div>

              {isLoading ? (
                <div className="mt-1 flex flex-col gap-1.5">
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static loading placeholder list
                      key={index}
                      className="h-5 rounded-md bg-bg-elevated animate-pulse"
                    />
                  ))}
                </div>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col gap-1">
                  {visibleEvents.map((event) => {
                    const positionedEvent: PositionedEvent = {
                      ...event,
                      column: 0,
                      totalColumns: 1,
                    }

                    const resource = resourceMap.get(event.resourceId)
                    const customEventContent = renderEvent?.({
                      event: positionedEvent,
                      resource,
                      view: 'month',
                      activeDate: date,
                      isMobile,
                    } as SchedulerEventRenderContext)

                    return (
                      <button
                        key={event.id}
                        type="button"
                        onClick={() => onEventClick?.(event)}
                        onMouseEnter={() => onEventHover?.(event)}
                        onMouseLeave={() => onEventHover?.(null)}
                        className={cn(
                          'w-full rounded-md border-l-[3px] text-left leading-tight transition-all hover:-translate-y-px hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                          isCompact ? 'px-1 py-0.5 text-[9px]' : 'px-1.5 py-1 text-[10px]',
                          VARIANT_ACCENT[event.variant ?? 'default'],
                          'bg-bg-elevated/95 text-text-secondary shadow-sm',
                        )}
                        aria-label={`${event.title} às ${event.startTime}`}
                      >
                        {customEventContent ?? (
                          <div className="flex items-center gap-1">
                            <span className="tabular-nums text-text-tertiary">
                              {event.startTime}
                            </span>
                            <span className="truncate font-medium">{event.title}</span>
                          </div>
                        )}
                      </button>
                    )
                  })}

                  {remainingEvents > 0 && (
                    <span className="px-1 text-[10px] font-medium text-text-tertiary">
                      +{remainingEvents} mais
                    </span>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
